import { FC, useState, useMemo, useCallback } from 'react';
import { Users } from 'lucide-react';
import { useToast } from '../../../ui/Toast';
import {
  useTherapistClients,
  useUpdateClientNotes,
} from '../../../../hooks/useTherapistDashboard';
import { ClientCard } from './ClientCard';
import { ClientSearch } from './ClientSearch';
import { ClientSummary } from './ClientSummary';
import { ClientsLoadingState } from './ClientsLoadingState';
import { ClientsEmptyState } from './ClientsEmptyState';
import { ClientsErrorState } from './ClientsErrorState';

export const ClientsSection: FC = () => {
  const { showToast } = useToast();
  const { data: clients, isLoading, error, refetch } = useTherapistClients();
  const updateNotesMutation = useUpdateClientNotes();

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedClientId, setExpandedClientId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});

  // Filter clients by search query
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    if (!searchQuery.trim()) return clients;

    const query = searchQuery.toLowerCase().trim();
    return clients.filter((client) => client.name.toLowerCase().includes(query));
  }, [clients, searchQuery]);

  // Handle client expand/collapse
  const handleToggleExpand = useCallback(
    (clientId: string) => {
      if (expandedClientId === clientId) {
        setExpandedClientId(null);
      } else {
        setExpandedClientId(clientId);
        // Initialize notes for this client if not already set
        const client = clients?.find((c) => c.id === clientId);
        if (client && !(clientId in editingNotes)) {
          setEditingNotes((prev) => ({
            ...prev,
            [clientId]: client.notes || '',
          }));
        }
      }
    },
    [expandedClientId, clients, editingNotes]
  );

  // Handle notes change
  const handleNotesChange = useCallback((clientId: string, notes: string) => {
    setEditingNotes((prev) => ({
      ...prev,
      [clientId]: notes,
    }));
  }, []);

  // Handle save notes
  const handleSaveNotes = useCallback(
    (clientId: string) => {
      const notes = editingNotes[clientId] || '';

      updateNotesMutation.mutate(
        { clientId, notes },
        {
          onSuccess: () => {
            showToast({
              type: 'success',
              title: 'Notes Saved',
              message: 'Client notes have been updated successfully.',
            });
          },
          onError: (err) => {
            showToast({
              type: 'error',
              title: 'Save Failed',
              message: err.message || 'Failed to save notes. Please try again.',
            });
          },
        }
      );
    },
    [editingNotes, updateNotesMutation, showToast]
  );

  // Get current notes value for a client
  const getNotesValue = useCallback(
    (clientId: string, defaultNotes?: string): string => {
      if (clientId in editingNotes) {
        return editingNotes[clientId] ?? defaultNotes ?? '';
      }
      return defaultNotes ?? '';
    },
    [editingNotes]
  );

  // Loading state
  if (isLoading) {
    return <ClientsLoadingState />;
  }

  // Error state
  if (error) {
    return <ClientsErrorState errorMessage={error.message} onRetry={() => refetch()} />;
  }

  // Empty state (no clients at all)
  if (!clients || clients.length === 0) {
    return <ClientsEmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Clients</h2>
          <p className="text-gray-600 dark:text-slate-400 mt-1">
            {clients.length} {clients.length === 1 ? 'client' : 'clients'} total
          </p>
        </div>

        {/* Search */}
        <ClientSearch searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
        <Users className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <p className="font-medium mb-1">Manage your clients</p>
          <p className="text-blue-600 dark:text-blue-300">
            Click on a client to view their details and add private notes. Notes are only visible
            to you and help you provide personalized care.
          </p>
        </div>
      </div>

      {/* No Results (from search) */}
      {filteredClients.length === 0 && searchQuery && (
        <ClientsEmptyState isSearchEmpty searchQuery={searchQuery} />
      )}

      {/* Client List */}
      <div className="space-y-3">
        {filteredClients.map((client) => (
          <ClientCard
            key={client.id}
            client={client}
            isExpanded={expandedClientId === client.id}
            onToggleExpand={() => handleToggleExpand(client.id)}
            editingNotes={getNotesValue(client.id, client.notes)}
            onNotesChange={(notes) => handleNotesChange(client.id, notes)}
            onSaveNotes={() => handleSaveNotes(client.id)}
            isSaving={
              updateNotesMutation.isPending &&
              updateNotesMutation.variables?.clientId === client.id
            }
          />
        ))}
      </div>

      {/* Summary Stats */}
      <ClientSummary clients={clients} />
    </div>
  );
};
